/**
 * Concerning Exposure Queries
 *
 * Frequently used queries for concerning security exposures and risks.
 * These queries are useful across all customer environments.
 *
 * To add new query: Add query object to CONCERNING_EXPOSURE_QUERIES array.
 * Query will automatically appear in defaultQueries.ts export.
 */

import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

import { QueryFolders } from './folders';

export const CONCERNING_EXPOSURE_QUERIES: SavedQuery[] = [
  {
    id: 'internet-exposed-databases',
    name: 'Internet-Exposed Databases',
    type: 'table-tree',
    description:
      'Find database services exposed to the internet. Includes common database ports: MySQL (3306), PostgreSQL (5432), MongoDB (27017), Redis (6379), Elasticsearch (9200), Oracle (1521), MS SQL (1434), Neo4j (7474/7687), Cassandra (9042), Couchbase (8091), and Derby (1527).',
    folderId: QueryFolders.CONCERNING_EXPOSURES,
    query: {
      node: {
        labels: ['Asset'],
        filters: [
          { field: 'private', operator: '=', value: true, not: true },
          {
            field: 'attackSurface',
            operator: 'IN',
            value: ['external'],
            reverseOperands: true,
          },
          { field: 'status', operator: '=', value: 'A' },
        ],
        relationships: [
          {
            label: 'HAS_PORT',
            target: {
              labels: ['Port'],
              filters: [
                {
                  field: 'port',
                  operator: 'IN',
                  value: [
                    [
                      1434, 1521, 1527, 3306, 5432, 6379, 7473, 7474, 7687,
                      8091, 9042, 9200, 27017,
                    ],
                  ],
                },
              ],
            },
          },
        ],
      },
    },
  },
  {
    id: 'internet-exposed-management-interfaces',
    name: 'Internet-Exposed Management Interfaces',
    type: 'table-tree',
    description:
      'Find remote management and administration services exposed to the internet. Includes SSH (22), Telnet (23), RDP (3389), VNC (5900-5902), WinRM (5985-5986), SNMP (161), and other common admin ports. These services are high-value targets for attackers.',
    folderId: QueryFolders.CONCERNING_EXPOSURES,
    query: {
      node: {
        labels: ['Asset'],
        filters: [
          { field: 'private', operator: '=', value: true, not: true },
          {
            field: '',
            operator: 'OR',
            value: [
              {
                field: 'attackSurface',
                operator: 'IN',
                value: ['external'],
                reverseOperands: true,
              },
            ],
          },
          { field: 'source', operator: '=', value: 'account', not: true },
          { field: 'status', operator: '=', value: 'A' },
        ],
        relationships: [
          {
            label: 'HAS_PORT',
            target: {
              labels: ['Port'],
              filters: [
                {
                  field: 'port',
                  operator: 'IN',
                  value: [
                    [
                      22, 23, 161, 2222, 3389, 5900, 5901, 5902, 5985, 5986,
                      8291, 10000,
                    ],
                  ],
                },
              ],
            },
          },
        ],
      },
    },
  },
  {
    id: 'internet-exposed-telecom-protocols',
    name: 'Internet-Exposed Telecom Protocols',
    type: 'table-tree',
    description:
      'Find telecom signaling and VoIP services exposed to the internet. Includes: RADIUS (1812-1813), GTP (2123/2152), MGCP (2427/2728), SMPP (2775-2776), SS7/SIGTRAN (2905-2906), H.248/MEGACO (2944-2945), Diameter (3868), SIP (5060-5061, 5080-5082), TR-069 (7547), PFCP (8805), NGAP (38412). These protocols assume trusted private networks and pose critical security risks when internet-exposed.',
    folderId: QueryFolders.CONCERNING_EXPOSURES,
    query: {
      node: {
        labels: ['Asset'],
        filters: [
          { field: 'private', operator: '=', value: true, not: true },
          {
            field: 'attackSurface',
            operator: 'IN',
            value: ['external'],
            reverseOperands: true,
          },
          { field: 'source', operator: '=', value: 'account', not: true },
          { field: 'status', operator: '=', value: 'A' },
        ],
        relationships: [
          {
            label: 'HAS_PORT',
            target: {
              labels: ['Port'],
              filters: [
                {
                  field: 'port',
                  operator: 'IN',
                  value: [
                    [
                      1812, 1813, 2123, 2152, 2427, 2728, 2775, 2776, 2905,
                      2906, 2944, 2945, 3868, 5060, 5061, 5080, 5081, 5082,
                      7547, 8805, 38412,
                    ],
                  ],
                },
              ],
            },
          },
        ],
      },
    },
  },
  {
    id: 'internet-exposed-login-portals',
    name: 'Internet-Exposed Login Portals',
    type: 'table',
    description:
      'Find detected web login portals. These represent potential targets for credential stuffing, brute force attacks, or phishing campaigns.',
    folderId: QueryFolders.CONCERNING_EXPOSURES,
    query: {
      node: {
        labels: ['Webpage'],
        filters: [
          {
            field: 'type',
            operator: '=',
            value: 'authentication',
          },
          { field: 'status', operator: '=', value: 'A' },
        ],
        relationships: [],
      },
    },
  },
  {
    id: 'internet-exposed-login-portals-without-sso',
    name: 'Internet-Exposed Login Portals Without SSO',
    type: 'table',
    description:
      'Find detected web login portals that have no SSO protection. These represent potential targets for credential stuffing, brute force attacks, or phishing campaigns.',
    folderId: QueryFolders.CONCERNING_EXPOSURES,
    query: {
      node: {
        labels: ['Webpage'],
        filters: [
          {
            field: 'type',
            operator: '=',
            value: 'authentication',
          },
          {
            field: '',
            operator: 'OR',
            value: [
              {
                field: 'sso_identified',
                operator: 'IS NULL',
                value: '',
              },
              {
                field: 'sso_identified',
                operator: '=',
                value: '{}',
              },
            ],
          },
          { field: 'status', operator: '=', value: 'A' },
        ],
        relationships: [],
      },
    },
  },
  {
    id: 'internet-exposed-chatbots',
    name: 'Internet-Exposed LLM Chatbots',
    type: 'table',
    description:
      'Find web applications hosting exposed LLM chatbot interfaces. These AI-powered endpoints may be vulnerable to prompt injection, jailbreaking, or data exfiltration attacks. Includes OpenAI-compatible APIs and custom chatbot implementations.',
    folderId: QueryFolders.CONCERNING_EXPOSURES,
    query: {
      node: {
        labels: ['Webpage'],
        filters: [
          {
            field: 'type',
            operator: '=',
            value: 'llm',
          },
          { field: 'status', operator: '=', value: 'A' },
        ],
        relationships: [
          {
            label: 'HAS_WEBPAGE',
            source: {
              labels: ['WebApplication'],
            },
          },
        ],
      },
    },
  },
  {
    id: 'internet-exposed-applications-without-waf',
    name: 'Applications Not Protected by WAF',
    type: 'table',
    description:
      'Find web applications on assets that have not been discovered by any Web Application Firewall integration. Checks asset origins for absence of Cloudflare WAF, Akamai WAF, AWS WAF, Vercel, Imperva, Fastly, and Palo Alto. Unprotected web applications are more susceptible to OWASP Top 10 attacks.',
    folderId: QueryFolders.CONCERNING_EXPOSURES,
    query: {
      node: {
        labels: ['WebApplication'],
        filters: [
          { field: 'status', operator: '=', value: 'A' },
          {
            field: 'origins',
            operator: 'ANY_IN',
            value: [
              [
                'cloudflare-waf',
                'akamai-waf',
                'awswaf',
                'vercel',
                'imperva',
                'fastly',
                'palo',
              ],
            ],
            not: true,
          },
        ],
      },
    },
  },
];
